import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./DiagnosticsListFacets.module.styl";

const KNOWN_EVENT_TYPES = [
  { value: "fatal-crash", label: t("filter.fatalCrash") },
  { value: "diagnostic", label: t("filter.diagnostic") },
  { value: "android-diagnostic", label: t("filter.androidDiagnostic") },
  { value: "nfc-inventory", label: t("filter.nfcInventory") }
];

const TABLET_ID_OPTIONS = [
  { value: "all", label: t("common.all") },
  { value: "present", label: t("filter.hasTabletId") },
  { value: "missing", label: t("filter.missingTabletId") }
];

export const DEFAULT_DIAGNOSTICS_LIST_FILTERS = Object.freeze({
  eventType: [],
  tabletId: "all",
  appVersion: [],
  deviceModel: []
});

const normalizeArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => String(item || "").trim()).filter(Boolean);
};

const normalizeOneOf = (value, allowed, fallback) => {
  const clean = String(value || "").trim();
  return allowed.includes(clean) ? clean : fallback;
};

export const normalizeDiagnosticsListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};

  return {
    eventType: normalizeArray(input.eventType),
    tabletId: normalizeOneOf(input.tabletId, TABLET_ID_OPTIONS.map((item) => item.value), "all"),
    appVersion: normalizeArray(input.appVersion),
    deviceModel: normalizeArray(input.deviceModel)
  };
};

export const isDefaultDiagnosticsListFilters = (value) => {
  const filters = normalizeDiagnosticsListFilters(value);

  return filters.eventType.length === 0 &&
    filters.tabletId === "all" &&
    filters.appVersion.length === 0 &&
    filters.deviceModel.length === 0;
};

export const getDiagnosticsListActiveFilterCount = (value) => {
  const filters = normalizeDiagnosticsListFilters(value);

  return filters.eventType.length +
    (filters.tabletId === "all" ? 0 : 1) +
    filters.appVersion.length +
    filters.deviceModel.length;
};

export const loadDiagnosticsFacetCounts = async (request = {}) => {
  const payload = {
    search: String(request?.search || "").trim(),
    filters: normalizeDiagnosticsListFilters(request?.filters || request?.value || {})
  };

  return rest.request(getServiceUri() + "diagnostics/facets", "POST", payload);
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const countForGrouped = (rows, value) => {
  if (!Array.isArray(rows)) {
    return undefined;
  }

  const row = rows.find((item) => item?.value === value);
  return row?.count;
};

const dynamicOptions = (rows, selectedValues) => {
  const byValue = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const value = String(row?.value || "").trim();
    if (value) {
      byValue.set(value, { value, label: value });
    }
  }

  for (const value of normalizeArray(selectedValues)) {
    if (!byValue.has(value)) {
      byValue.set(value, { value, label: value });
    }
  }

  return Array.from(byValue.values());
};

const eventTypeOptions = (counts, selectedValues) => {
  const keys = new Set([
    ...KNOWN_EVENT_TYPES.map((item) => item.value),
    ...Object.keys(counts?.eventType || {}),
    ...normalizeArray(selectedValues)
  ]);

  return Array.from(keys).map((value) => ({
    value,
    label: KNOWN_EVENT_TYPES.find((item) => item.value === value)?.label || value
  }));
};

const FacetSection = ({ title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const CheckboxOption = ({ label, selected, count, disabled, onClick }) => (
  <button
    type="button"
    className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
    disabled={disabled}
    onClick={onClick}
  >
    <span className={styles.checkBox}>{selected ? "✓" : ""}</span>
    <span className={styles.optionLabel}>{label}</span>
    {count !== undefined ? <span className={styles.count}>{count}</span> : null}
  </button>
);

const Chip = ({ label, onRemove }) => (
  <button type="button" className={styles.chip} onClick={onRemove}>
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

export const DiagnosticsListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton}
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}><TuneIcon fontSize="inherit" /></span>
      <span className={styles.filterButtonLabel}>{t("common.filters")}</span>
      {activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}
    </button>
  );
};

export const DiagnosticsListFacetChips = ({ controller }) => {
  const filters = normalizeDiagnosticsListFilters(controller?.value);

  if (isDefaultDiagnosticsListFilters(filters)) {
    return null;
  }

  const counts = controller?.counts || {};
  const eventLabels = new Map(eventTypeOptions(counts, filters.eventType).map((item) => [item.value, item.label]));
  const set = (patch) => controller.patchValue(patch);

  return (
    <div className={styles.chipsBar}>
      {filters.eventType.map((value) => (
        <Chip
          key={`event-${value}`}
          label={`${t("common.event")}: ${eventLabels.get(value) || value}`}
          onRemove={() => set({ eventType: filters.eventType.filter((item) => item !== value) })}
        />
      ))}

      {filters.tabletId !== "all" ? (
        <Chip
          label={`${t("common.tabletId")}: ${TABLET_ID_OPTIONS.find((item) => item.value === filters.tabletId)?.label || filters.tabletId}`}
          onRemove={() => set({ tabletId: "all" })}
        />
      ) : null}

      {filters.appVersion.map((value) => (
        <Chip
          key={`version-${value}`}
          label={`${t("common.version")}: ${value}`}
          onRemove={() => set({ appVersion: filters.appVersion.filter((item) => item !== value) })}
        />
      ))}

      {filters.deviceModel.map((value) => (
        <Chip
          key={`device-${value}`}
          label={`${t("common.device")}: ${value}`}
          onRemove={() => set({ deviceModel: filters.deviceModel.filter((item) => item !== value) })}
        />
      ))}

      <button type="button" className={styles.clearAll} onClick={controller.reset}>{t("common.clearAll")}</button>
    </div>
  );
};

const DiagnosticsListFacets = ({ controller }) => {
  const filters = normalizeDiagnosticsListFilters(controller?.value);
  const counts = controller?.counts || {};
  const set = (patch) => controller.patchValue(patch);
  const appVersionOptions = dynamicOptions(counts.appVersion, filters.appVersion);
  const deviceOptions = dynamicOptions(counts.deviceModel, filters.deviceModel);

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>{t("common.filters")}</div>
          <div className={styles.drawerSubtitle}>{controller.countsLoading ? t("common.updatingCounts") : t("filter.refineDiagnostics")}</div>
        </div>

        <button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <FacetSection title={t("filter.eventType")}>
          {eventTypeOptions(counts, filters.eventType).map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.eventType.includes(option.value)}
              count={counts?.eventType?.[option.value]}
              onClick={() => set({ eventType: toggleArrayValue(filters.eventType, option.value) })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("common.tabletId")}>
          {TABLET_ID_OPTIONS.map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.tabletId === option.value}
              count={option.value === "present" || option.value === "missing" ? counts?.tabletId?.[option.value] : counts?.total}
              onClick={() => set({ tabletId: option.value })}
            />
          ))}
        </FacetSection>

        {appVersionOptions.length ? (
          <FacetSection title={t("common.appVersion")}>
            {appVersionOptions.map((option) => (
              <CheckboxOption
                key={option.value}
                label={option.label}
                selected={filters.appVersion.includes(option.value)}
                count={countForGrouped(counts.appVersion, option.value)}
                onClick={() => set({ appVersion: toggleArrayValue(filters.appVersion, option.value) })}
              />
            ))}
          </FacetSection>
        ) : null}

        {deviceOptions.length ? (
          <FacetSection title={t("common.device")}>
            {deviceOptions.map((option) => (
              <CheckboxOption
                key={option.value}
                label={option.label}
                selected={filters.deviceModel.includes(option.value)}
                count={countForGrouped(counts.deviceModel, option.value)}
                onClick={() => set({ deviceModel: toggleArrayValue(filters.deviceModel, option.value) })}
              />
            ))}
          </FacetSection>
        ) : null}
      </div>

      <div className={styles.drawerFooter}>
        <button type="button" className={styles.secondaryAction} onClick={controller.reset} disabled={controller.activeCount === 0}>
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>{t("common.done")}</button>
      </div>
    </div>
  );
};

export const DIAGNOSTICS_LIST_FACETS = {
  storageKey: "haiti.diagnostics.facets.v4",
  overlayLabel: t("filter.diagnosticFilters"),
  defaultValue: DEFAULT_DIAGNOSTICS_LIST_FILTERS,
  normalizeValue: normalizeDiagnosticsListFilters,
  isDefaultValue: isDefaultDiagnosticsListFilters,
  getActiveCount: getDiagnosticsListActiveFilterCount,
  loadCounts: loadDiagnosticsFacetCounts,
  renderButton: (controller) => <DiagnosticsListFacetButton controller={controller} />,
  renderDrawer: (controller) => <DiagnosticsListFacets controller={controller} />,
  renderChips: (controller) => <DiagnosticsListFacetChips controller={controller} />
};

export default DiagnosticsListFacets;
